import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Droplets, Sparkles, ShieldCheck } from "lucide-react";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { useSeo, faqJsonLd, SEO_KEYWORDS } from "@/hooks/useSeo";

const SECTIONS = [
    {
        icon: Leaf,
        title: "Why does hair fall happen? (Baal kyun girte hain?)",
        body: "Hair fall in Pakistan is mostly caused by stress, hard water, iron or vitamin D deficiency, hormonal changes (after pregnancy or PCOS), dandruff and heat styling. Losing 50–100 hairs a day is normal; more than that, thinning at the crown or a widening parting means your follicles need help.",
    },
    {
        icon: Droplets,
        title: "How rosemary oil helps hair growth",
        body: "Rosemary improves blood circulation to the scalp and helps protect follicles, which is why it is one of the most popular natural hair growth ingredients worldwide. A lightweight rosemary hair serum absorbs faster than heavy oils, so you can use it daily without a greasy scalp.",
    },
    {
        icon: Sparkles,
        title: "Daily routine: baal girna ka ilaj at home",
        body: "1) Apply 6–8 drops of rosemary hair growth serum on a dry scalp at night. 2) Massage for 3–5 minutes with fingertips or a scalp massager comb. 3) Leave overnight and wash with a mild shampoo. 4) Eat protein, eggs, lentils, spinach and dates. 5) Sleep 7–8 hours and avoid tight hairstyles. Results usually show in 4–8 weeks of regular use.",
    },
    {
        icon: ShieldCheck,
        title: "Why use a scalp massager comb?",
        body: "A serum applicator comb sends the serum straight to the roots instead of your palms and hair lengths, so less product is wasted. Gentle massage also relaxes the scalp and boosts circulation — making every drop of your hair fall treatment work harder.",
    },
];

const FAQS = [
    { q: "What is the best hair growth serum in Pakistan?", a: "Look for a PCSIR certified, rosemary-based serum that is lightweight and safe for daily use. Resham Drops Dermalist Rosemary Hair Growth Serum is PCSIR certified and delivered cash on delivery across Pakistan." },
    { q: "Baal girna kaise roke? (How to stop hair fall?)", a: "Use a rosemary hair serum daily with a 3–5 minute scalp massage, eat protein and iron-rich food, reduce heat styling and treat dandruff. Most people see less hair fall within 4–8 weeks." },
    { q: "Is rosemary oil good for hair growth?", a: "Yes. Rosemary supports scalp circulation and healthy follicles, which is why it is widely used for thinning hair and hair fall." },
    { q: "How long does a hair growth serum take to work?", a: "Reduced hair fall is often noticed in 3–4 weeks; visible new baby hair usually appears after 6–12 weeks of consistent nightly use." },
    { q: "Can women and men both use rosemary hair serum?", a: "Yes, it is suitable for both men and women with all hair types, including oily and dry scalps." },
    { q: "Do you deliver all over Pakistan with cash on delivery?", a: "Yes. Resham Drops delivers to Karachi, Lahore, Islamabad, Rawalpindi, Faisalabad, Multan, Peshawar and every city in Pakistan, with cash on delivery or Easypaisa, JazzCash and bank transfer." },
    { q: "Ganjapan ka ilaj mumkin hai? (Can baldness be treated?)", a: "Early thinning and hair fall can often be improved with consistent scalp care and a rosemary serum. For advanced baldness please consult a dermatologist." },
];

export default function HairCareGuidePage() {
    useSeo({
        title: "Hair Fall Treatment Guide Pakistan | Baal Girna Ka Ilaj · Resham Drops",
        description: "Complete guide to stop hair fall in Pakistan: causes, rosemary hair oil benefits, daily routine with a scalp massager comb, and FAQs on baal girna ka ilaj.",
        keywords: SEO_KEYWORDS,
        path: "/hair-care-guide",
        jsonLd: faqJsonLd(FAQS),
    });

    return (
        <div data-testid="hair-guide-page" className="min-h-screen bg-cream text-forest-900 font-sans">
            <AnnouncementBar />
            <Header />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
                <div className="flex items-center justify-between gap-4">
                    <span className="inline-block bg-forest-800 text-gold-400 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                        Hair care guide
                    </span>
                    <Link
                        to="/ur/hair-care-guide"
                        data-testid="guide-lang-toggle-ur"
                        style={{ letterSpacing: 0 }} className="font-urdu text-sm font-bold text-forest-700 border border-forest-200 rounded-full px-4 py-1 hover:bg-forest-800 hover:text-gold-400 transition-colors"
                    >
                        اردو
                    </Link>
                </div>
                <h1 className="font-serif font-bold text-forest-800 text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4">
                    Hair fall treatment guide: baal girna ka ilaj
                </h1>
                <p className="text-forest-900/70 text-sm sm:text-base mt-5 max-w-2xl">
                    Everything you need to know about stopping hair fall and growing thicker hair naturally — written for Pakistani hair, water and weather.
                </p>

                <div className="mt-12 space-y-6">
                    {SECTIONS.map((s, i) => (
                        <article
                            key={s.title}
                            data-testid={`guide-section-${i}`}
                            className="bg-white rounded-2xl border border-forest-100 shadow-sm p-6 sm:p-8"
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-11 h-11 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center shrink-0">
                                    <s.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="font-serif font-bold text-forest-800 text-base md:text-lg">{s.title}</h2>
                                    <p className="text-sm sm:text-base text-forest-900/75 mt-2 leading-relaxed">{s.body}</p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <GuideCta />

                <section className="mt-14" aria-labelledby="guide-faq">
                    <h2 id="guide-faq" className="font-serif font-bold text-forest-800 text-base md:text-lg">
                        Frequently asked questions
                    </h2>
                    <div className="mt-5 space-y-3">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                data-testid={`guide-faq-${i}`}
                                className="group bg-white rounded-xl border border-forest-100 p-5 open:shadow-sm"
                            >
                                <summary className="cursor-pointer font-bold text-forest-900 text-sm sm:text-base list-none flex justify-between gap-4">
                                    {f.q}
                                    <span className="text-gold-600 transition-transform group-open:rotate-45">+</span>
                                </summary>
                                <p className="text-sm text-forest-900/75 mt-3 leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

function GuideCta() {
    return (
        <div className="mt-12 bg-forest-900 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5 border border-gold-500/30">
            <div>
                <p className="font-serif font-bold text-gold-400 text-lg">Start your hair fall treatment today</p>
                <p className="text-cream/75 text-sm mt-1">PCSIR certified rosemary hair growth serum · Cash on delivery across Pakistan</p>
            </div>
            <div className="flex flex-wrap gap-3">
                <Link
                    to="/product/dermalist-rosemary-hair-growth-serum"
                    data-testid="guide-cta-serum"
                    className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-forest-900 font-bold text-sm px-5 py-3 rounded-full transition-colors"
                >
                    Rosemary serum <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                    to="/product/crystal-serum-applicator-scalp-massager-comb"
                    data-testid="guide-cta-comb"
                    className="inline-flex items-center gap-2 border border-gold-500/60 text-gold-400 hover:bg-forest-800 font-bold text-sm px-5 py-3 rounded-full transition-colors"
                >
                    Scalp massager comb
                </Link>
            </div>
        </div>
    );
}
